import { useCallback } from 'react';

import { TableState } from 'react-table';
import { uniq, isNil } from 'lodash';

import { TaskAssignedStateEnum } from '../../../types/common.consts';
import { EXCEPTIONS_COLUMN_IDS } from '../../../consts';
import type { TableConfigObjectAdapter } from '../../../stores/alerts-view/table-config-object-adapter';

const HIDDEN_COLUMN_IDS = [];
const STATIC_COLUMN_IDS = [EXCEPTIONS_COLUMN_IDS.SELECTION_COLUMN];

const getFiltersState = (externalFilters: { searchText?: string; [key: string]: unknown }) => {
	const nextFilters = [];

	const { teams, alerts, routes, companies, searchText, assignedState, alertOwner, severity } = externalFilters;

	if ((teams as Array<unknown>)?.length) {
		nextFilters.push({ id: EXCEPTIONS_COLUMN_IDS.TEAM_ID, value: teams });
	}
	if ((alerts as Array<unknown>)?.length) {
		nextFilters.push({ id: EXCEPTIONS_COLUMN_IDS.ALERT_TYPE, value: alerts });
	}
	if ((routes as Array<unknown>)?.length) {
		nextFilters.push({ id: EXCEPTIONS_COLUMN_IDS.ROUTE, value: routes });
	}
	if ((companies as Array<unknown>)?.length) {
		nextFilters.push({ id: EXCEPTIONS_COLUMN_IDS.COMPANY_ID, value: companies });
	}
	if (searchText) {
		nextFilters.push({ id: EXCEPTIONS_COLUMN_IDS.EXTERNAL_ID, value: searchText?.trim() });
	}
	if (assignedState) {
		nextFilters.push({
			id: EXCEPTIONS_COLUMN_IDS.USER_ID,
			value: assignedState === TaskAssignedStateEnum.ASSIGNED
		});
	}
	if (!isNil(alertOwner)) {
		nextFilters.push({
			id: EXCEPTIONS_COLUMN_IDS.ALERT_OWNER,
			value: alertOwner
		});
	}

	if (!isNil(severity)) {
		nextFilters.push({
			id: EXCEPTIONS_COLUMN_IDS.SEVERITY,
			value: severity
		});
	}

	return nextFilters;
};

// TODO: improve state memoization mechanism
const useControlledTableState = <T extends Record<string, unknown>>(tableConfig: TableConfigObjectAdapter) => {
	const { tableNormalizedFilters, tableColumnOrder, hiddenTableColumns, tableGroupBy } = tableConfig;

	return useCallback(
		(state: TableState<T>): TableState<T> => {
			const nextState: TableState<T> = { ...state };

			nextState.filters = getFiltersState(tableNormalizedFilters);
			nextState.columnOrder = STATIC_COLUMN_IDS.concat(tableColumnOrder);
			nextState.hiddenColumns = uniq(hiddenTableColumns.concat(HIDDEN_COLUMN_IDS));
			nextState.groupBy = tableGroupBy;

			return nextState;
		},
		[tableNormalizedFilters, hiddenTableColumns, tableColumnOrder, tableGroupBy]
	);
};

export default useControlledTableState;
